import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import conversationService, {
  toLiveChatError,
  type ContactActionResponse,
  type ContactThread,
  type TakeoverState,
} from '@/services/conversationService';
import { markChannelBlocked } from '@/lib/channelHealth';
import { appendThreadMessage, optimisticTakeover } from '@/lib/liveConversation';
import type { HandoffBusy } from './HandoffStatusBar';

/** Functional update of the open contact thread, owned by whoever loaded it. */
export type ThreadUpdater = (update: (thread: ContactThread) => ContactThread) => void;

/** The server accepts one typing ping per 20 s (livecenter §5). */
export const TYPING_HEARTBEAT_MS = 20_000;

export interface ContactActionsOptions {
  /**
   * The server refused a send because the WhatsApp number is blocked. The
   * thread already shows the banner; this lets the owner fetch the reason.
   */
  onChannelBlocked?: () => void;
}

export interface ContactActions {
  busy: HandoffBusy;
  sending: boolean;
  takeover: () => Promise<void>;
  release: () => Promise<void>;
  extend: () => Promise<void>;
  resolveCallback: () => Promise<void>;
  /** Resolves true when the backend accepted the message. */
  send: (text: string) => Promise<boolean>;
  /** Staff is typing; throttled to one request per 20 s. */
  typingHeartbeat: () => void;
}

/**
 * Handoff actions and sending for one WhatsApp contact — optimistic where the
 * outcome is certain, with rollback and a toast on failure. The stream
 * (`whatsapp.takeover` / `whatsapp.message`) later confirms or corrects.
 */
export function useContactActions(
  contactKey: string,
  updatePageThread: ThreadUpdater,
  actorEmail: string | null,
  { onChannelBlocked }: ContactActionsOptions = {},
): ContactActions {
  const [busy, setBusy] = useState<HandoffBusy>(null);
  const [sending, setSending] = useState(false);
  const lastTypingRef = useRef(0);

  // A request may resolve after the user opened another contact; its result
  // must never land on that other chat.
  const updateThread = useCallback<ThreadUpdater>(
    (update) => updatePageThread((t) => (t.contact_key === contactKey ? update(t) : t)),
    [updatePageThread, contactKey],
  );

  const applyResponse = useCallback(
    (res: ContactActionResponse) => {
      if (res && 'takeover' in res) updateThread((t) => ({ ...t, takeover: res.takeover ?? null }));
    },
    [updateThread],
  );

  /**
   * `optimistic` = the state shown until the answer; `undefined` = keep the
   * current state (no safe guess, e.g. how far +5 min moves the deadline).
   */
  const run = useCallback(
    async (
      kind: Exclude<HandoffBusy, null>,
      request: () => Promise<ContactActionResponse>,
      success: string | null | ((res: ContactActionResponse) => string),
      optimistic?: TakeoverState | null,
    ) => {
      let previous: TakeoverState | null | undefined;
      const hasOptimistic = optimistic !== undefined;
      if (hasOptimistic) {
        updateThread((t) => {
          previous = t.takeover;
          return { ...t, takeover: optimistic };
        });
      }
      setBusy(kind);
      try {
        const res = await request();
        applyResponse(res);
        const message = typeof success === 'function' ? success(res) : success;
        if (message) toast.success(message);
      } catch (error) {
        // Roll back only our own optimistic state: a `whatsapp.takeover`
        // that replaced it meanwhile is newer truth from the server.
        if (hasOptimistic) {
          updateThread((t) => (t.takeover === optimistic ? { ...t, takeover: previous ?? null } : t));
        }
        toast.error(toLiveChatError(error).message);
      } finally {
        setBusy(null);
      }
    },
    [updateThread, applyResponse],
  );

  const takeover = useCallback(
    () =>
      run(
        'takeover',
        () => conversationService.contactTakeover(contactKey),
        'Gespräch übernommen – der Agent pausiert.',
        optimisticTakeover(actorEmail),
      ),
    [run, contactKey, actorEmail],
  );

  // Release and resolve wait for the server instead of showing `null` early:
  // a failed request could not tell its own `null` from one the stream set
  // meanwhile, and its rollback would bring back a takeover that ended.
  const release = useCallback(
    () =>
      run('release', () => conversationService.contactRelease(contactKey), (res) =>
        // A callback wish survives a hand-back (only "Erledigt" closes it).
        res?.takeover?.active && res.takeover.state === 'callback'
          ? 'Zurück an den Agenten – der Rückmeldewunsch bleibt offen.'
          : 'Gespräch an den Agenten zurückgegeben.',
      ),
    [run, contactKey],
  );

  const extend = useCallback(
    () => run('extend', () => conversationService.contactExtend(contactKey), null),
    [run, contactKey],
  );

  const resolveCallback = useCallback(
    () => run('resolve', () => conversationService.resolveCallback(contactKey), 'Rückmeldung als erledigt markiert.'),
    [run, contactKey],
  );

  const send = useCallback(
    async (text: string): Promise<boolean> => {
      setSending(true);
      try {
        const res = await conversationService.contactSendMessage(contactKey, text);
        updateThread((t) => {
          const withMessage = res.message ? appendThreadMessage(t, res.message) : t;
          // Sending opens/extends the takeover server-side; the answer carries it.
          const takeoverState =
            res.takeover !== undefined ? res.takeover : optimisticTakeover(actorEmail ?? t.takeover?.taken_by ?? null);
          return { ...withMessage, takeover: takeoverState };
        });
        lastTypingRef.current = 0;
        return true;
      } catch (error) {
        const liveError = toLiveChatError(error);
        if (liveError.code === 'reply_window_closed') {
          updateThread((t) => ({ ...t, can_reply: false }));
        }
        if (liveError.code === 'whatsapp_channel_blocked') {
          // The number is blocked: banner on now (the stream may have missed
          // it), composer closed, draft untouched — the caller only clears it on success.
          updateThread(markChannelBlocked);
          onChannelBlocked?.();
        }
        toast.error(liveError.message);
        return false;
      } finally {
        setSending(false);
      }
    },
    [contactKey, updateThread, actorEmail, onChannelBlocked],
  );

  const typingHeartbeat = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingRef.current < TYPING_HEARTBEAT_MS) return;
    lastTypingRef.current = now;
    conversationService.contactTyping(contactKey).then(applyResponse, () => {
      // Best effort: a lost ping only means the deadline is not pushed this
      // time; the next keystroke after 20 s tries again. Nothing to tell the user.
      lastTypingRef.current = 0;
    });
  }, [contactKey, applyResponse]);

  return { busy, sending, takeover, release, extend, resolveCallback, send, typingHeartbeat };
}

export default useContactActions;
